import { Board } from "@sportactics/board-editing";
import { OfflineError } from "@sportactics/errors";
import { useEffect, useEffectEvent } from "react";
import { useSWRConfig } from "swr";

import { generateUniqueName, swallow } from "~utils";

import { useUser } from "../user";
import {
  useFormationFirestorePersistence,
  useFormationFirestorePersistenceReturn,
} from "./formation-firestore-persistence";
import {
  useFormationLocalPersistence,
  useFormationLocalPersistenceReturn,
} from "./formation-local-persistence";
import { getFormationsFirestoreSWRKey } from "./use-formations-firestore-swr";
import { getFormationsLocalSWRKey } from "./use-formations-local-swr";

// Same once-per-sign-in shape as `useMigrateBoardsEffect`.
export function useMigrateFormationsEffect(): void {
  const { user } = useUser();
  const migrate = useEffectEvent(useMigrate());

  useEffect(() => {
    if (!user.authenticated) return;

    void swallow(migrate, OfflineError)();
  }, [user.authenticated, user.id]);
}

function useMigrate(): () => Promise<void> {
  const { user } = useUser();
  const { mutate } = useSWRConfig();
  const local = useFormationLocalPersistence();
  const firestore = useFormationFirestorePersistence();

  const userId = user.id;

  return async (): Promise<void> => {
    const localFormations = await local.listFormations();
    if (Object.keys(localFormations).length === 0) return;

    const existing = await firestore.listFormations(userId);
    const targets = assignTargetNames(localFormations, Object.keys(existing));
    const migrated = Object.fromEntries(
      targets.map(({ targetName, board }) => [targetName, board]),
    );
    // Merged over the cloud list just read, not only over the cache: the cache is
    // empty when no list is open, and a merge over nothing would commit the migrated
    // formations alone as the account's list.
    const merge = (current?: Record<string, Board>): Record<string, Board> => ({
      ...existing,
      ...current,
      ...migrated,
    });

    // Populates the Firestore cache the moment the migration starts, so a list
    // opened before the writes land already shows the migrated formations. No
    // revalidation afterwards: the resolved value is what was just written.
    await mutate(
      getFormationsFirestoreSWRKey(userId),
      async (current?: Record<string, Board>) => {
        await Promise.all(
          targets.map((target) =>
            migrateFormation(userId, target, firestore, local),
          ),
        );
        return merge(current);
      },
      { optimisticData: merge, revalidate: false },
    );

    // Every local copy is gone now, and the local store is shared by every anonymous
    // user of the device: the list must not resurface from the cache for the next one.
    await mutate(getFormationsLocalSWRKey(), {}, { revalidate: false });
  };
}

function assignTargetNames(
  localFormations: Record<string, Board>,
  existingNames: readonly string[],
): FormationMigrationTarget[] {
  return Object.entries(localFormations).reduce<FormationMigrationTarget[]>(
    (targets, [name, board]) => {
      const usedNames = [...existingNames, ...targets.map((t) => t.targetName)];
      const targetName = generateUniqueName(name, usedNames);
      return [...targets, { name, targetName, board }];
    },
    [],
  );
}

// The local copy is deleted only once the Firestore write has actually landed.
async function migrateFormation(
  userId: string,
  { name, targetName, board }: FormationMigrationTarget,
  firestore: useFormationFirestorePersistenceReturn,
  local: useFormationLocalPersistenceReturn,
): Promise<void> {
  await firestore.createFormation(userId, targetName, board);
  await local.deleteFormation(name);
}

type FormationMigrationTarget = {
  name: string;
  targetName: string;
  board: Board;
};
